import { useState, useEffect, useMemo } from 'react'
import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa'
import { useSelector } from 'react-redux'

import PageHeader from '../../../../shared/PageHeader'
import ReportFilters from '../../../../shared/ReportFilters'
import ReportJobsList from '../../../../shared/ReportJobsList'
import useAsyncReport from '../../../../hooks/useAsyncReport'
import { fetchData } from '../../../../helpers/fetchData'
import {
  addResidentialReportJob,
  updateResidentialReportJob,
  removeResidentialReportJob
} from '../../../../redux/slices/AppSlice'
import { validateOneMonthRange, getMinDate, getOneMonthMaxDate } from '../../../../utils/dateRangeValidation'

const ResidentialReportAsync = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const jobs = useSelector(state => state.app.residentialReportJobs || [])

  const { filters: payloadData, setFilters: setPayloadData, resetFilters } = usePersistedFilters(
    'residential_report_async_new',
    {
      start_date: moment().subtract(1, 'day').toDate(),
      end_date: moment().toDate(),
      department_ids: [],
      practitioner_id: '',
      nationality: '',
      category: '',
      item_wise: '',
      format: 'pdf'
    }
  )

  const [departments, setDepartments] = useState([])
  const [practitioners, setPractitioners] = useState([])

  const { loading, downloadingJobs, startJob, downloadReport, removeJob, retryJob } = useAsyncReport({
    addJobAction: addResidentialReportJob,
    updateJobAction: updateResidentialReportJob,
    removeJobAction: removeResidentialReportJob,
    createEndpoint: '/v3/reports/nationality/summary/jobs',
    statusEndpoint: '/v3/reports/nationality/summary/jobs',
    downloadEndpoint: '/v3/reports/nationality/summary/jobs',
    fileNamePrefix: 'residential_report',
    existingJobs: jobs,
    buildPayload: (filters, format) => ({
      start_date: moment(filters.start_date).format('YYYY-MM-DD'),
      end_date: moment(filters.end_date).format('YYYY-MM-DD'),
      department_ids: filters.department_ids.length > 0 ? [filters.department_ids] : undefined,
      practitioner_ids: filters.practitioner_id ? [filters.practitioner_id] : undefined,
      nationality: filters.nationality || undefined,
      category: filters.category || "all",
      item_wise: filters.item_wise === 'item_wise',
      format
    })
  })

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const res = await fetchData('POST', '/v3/department/getList', {})
        if (res?.data) {
          setDepartments(res.data.map(d => ({ label: d.name_en, value: d.id })))
        }
      } catch (error) {
        console.log(error)
      }
    }
    loadDepartments()
  }, [])

  useEffect(() => {
    const loadPractitioners = async () => {
      try {
        const res = await fetchData('POST', '/v3/practitioner/getList', {})
        if (res?.data) {
          setPractitioners(res.data.map(p => ({ label: p.name, value: p.practitioner_id })))
        }
      } catch (error) {
        console.log(error)
      }
    }
    loadPractitioners()
  }, [])

  const handleGenerate = async () => {
    if (!validateOneMonthRange(payloadData.start_date, payloadData.end_date, t)) return
    await startJob(payloadData, payloadData.format)
  }

  const handleReset = () => {
    resetFilters()
  }

  const handleChange = e => setPayloadData(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const filterConfig = useMemo(() => [
    {
      type: 'date',
      name: 'start_date',
      label: 'Start Date',
      width: 'w-[160px]'
    },
    {
      type: 'date',
      name: 'end_date',
      label: 'End Date',
      width: 'w-[160px]',
      minDate: getMinDate(payloadData.start_date),
      maxDate: getOneMonthMaxDate(payloadData.start_date)
    },
    {
      type: 'dropdown',
      name: 'department_ids',
      label: 'Departments',
      options: departments,
      placeholder: 'All Departments',
      filter: true,
      multiple: true,
      width: 'w-[200px]'
    },
    {
      type: 'dropdown',
      name: 'practitioner_id',
      label: 'Practitioner',
      options: practitioners,
      placeholder: 'All Practitioners',
      filter: true,
      width: 'w-[200px]'
    },
    {
      type: 'dropdown',
      name: 'nationality',
      label: 'Residential',
      options: [
        { label: t('All'), value: '' },
        { label: t('Saudi'), value: 'saudi' },
        { label: t('Non-Saudi'), value: 'non-saudi' }
      ],
      width: 'w-[160px]'
    },
    {
      type: 'dropdown',
      name: 'category',
      label: 'Category',
      options: [
        { label: t('All'), value: '' },
        { label: t('Advance Payment'), value: 'advance_payment' }
      ],
      width: 'w-[160px]'
    },
    {
      type: 'dropdown',
      name: 'item_wise',
      label: 'Report Type',
      options: [
        { label: t('Invoice Wise'), value: '' },
        { label: t('Item Wise'), value: 'item_wise' }
      ],
      width: 'w-[160px]'
    },
    {
      type: 'dropdown',
      name: 'format',
      label: 'Format',
      options: [
        { label: 'Excel', value: 'excel' },
        { label: 'PDF', value: 'pdf' }
      ],
      width: 'w-[160px]'
    }
  ], [departments, practitioners, payloadData.start_date, t])

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <div className='flex items-center justify-between'>
        <PageHeader
          title={t('Residential Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <ReportFilters
        filters={payloadData}
        onChange={handleChange}
        filterConfig={filterConfig}
        onGenerate={handleGenerate}
        onReset={handleReset}
        loading={loading}
      />

      <ReportJobsList
        jobs={jobs}
        onDownload={downloadReport}
        onRemove={removeJob}
        onRetry={retryJob}
        downloadingJobs={downloadingJobs}
        reportTitle={t('Residential Report')}
      />
    </div>
  )
}

export default ResidentialReportAsync
